"use client";

import { useState } from "react";
import Image from "next/image";
import styles from "./Features.module.css";

const featureTabs = [
    {
        id: "simob",
        tabLabel: "SIMOB - ERP",
        title: "Gestão central da operação imobiliária",
        description:
            "O SIMOB centraliza locação, financeiro, contratos e relatórios em um único ambiente. Elimina planilhas paralelas e traz visibilidade real para cada área.",
        points: [
            "Gestão completa de contratos de locação",
            "Reajustes automáticos por índice (IGPM, IPCA, INPC)",
            "Repasses a proprietários e beneficiários (com split e porcentagem)",
            "Histórico completo de movimentações por imóvel e pessoa",
            "Controle de inadimplência e negociações",
        ],
        cta: "Explorar o SIMOB",
        href: "/sistema-para-imobiliaria",
        metrics: [
            { value: "1.847", label: "Contratos ativos" },
            { value: "R$2,5M", label: "Faturamento/mês" },
            { value: "2,3%", label: "Inadimplência" },
        ],
    },
    {
        id: "locacao",
        tabLabel: "Locação",
        title: "Locação sem retrabalho e sem perder prazo",
        description:
            "Gerencie toda a carteira de locações em um painel unificado. Contratos, reajustes, repasses, seguros e vencimentos, tudo no prazo certo, sem depender de planilha.",
        points: [
            "Alertas automáticos de vencimento e renovação",
            "Geração de contratos com modelos personalizados",
            "Assinatura eletrônica ilimitada integrada",
            "Gestão de garantias (fiança e incêndio)",
            "Gestão de manutenções por imóvel ou contrato",
            "Vistorias vinculadas ao contrato",
        ],
        cta: "Explorar Locação",
        href: "/sistema-para-imobiliaria/locacao",
        metrics: [
            { value: "428", label: "Vencimentos monitorados" },
            { value: "98%", label: "Contratos em dia" },
            { value: "24h", label: "Alertas antecipados" },
        ],
    },
    {
        id: "crm",
        tabLabel: "CRM & Site",
        title: "CRM com navegação rápida e busca eficiente",
        description:
            "O MOOV conecta seu site e portais em um único funil. Sua equipe comercial foca em fechar, não em organizar planilha.",
        points: [
            "Site integrado com busca e filtros",
            "Funil visual com etapas personalizadas",
            "Captura e distribuição automática de leads do site e portal (chaves na mão)",
            "Automações de follow-up e nutrição",
            "Relatórios gerenciais por canal e corretor",
        ],
        cta: "Explorar CRM & Site",
        href: "/crm-para-imobiliarias/gestao-de-leads",
        metrics: [
            { value: "312", label: "Leads capturados" },
            { value: "41%", label: "Taxa de avanço" },
            { value: "18", label: "Canais conectados" },
        ],
    },
    {
        id: "bi",
        tabLabel: "Business Intelligence",
        title: "Dados que guiam decisões de verdade",
        description:
            "Dashboards e relatórios em tempo real para cada área. Pare de tomar decisões no escuro, enxergue a operação completa com os dados que realmente importam com nosso Business Intelligence.",
        points: [
            "Visualização de indicadores-chave de desempenhos por área e período",
            "Indicadores de locação, vacância e inadimplência",
            "Performance por corretor e canal de origem",
            "Exportação/Importação para Excel e relatórios agendados",
        ],
        cta: "Explorar BI",
        href: "/sistema-para-imobiliaria/bi",
        metrics: [
            { value: "12", label: "Painéis ativos" },
            { value: "100%", label: "Dados integrados" },
            { value: "D+0", label: "Atualização" },
        ],
    },
    {
        id: "sitalk",
        tabLabel: "Sitalk",
        title: "App com sua marca para inquilinos e proprietários",
        description:
            "O Sitalk leva a identidade da sua imobiliária para o celular de quem você atende. Inquilinos e proprietários acessam tudo sem precisar ligar.",
        points: [
            "Ver boletos",
            "Abrir chamados",
            "Acompanhar repasses",
        ],
        cta: "Explorar Sitalk",
        href: "/aplicativos-para-imobiliarias",
        metrics: [
            { value: "24/7", label: "Acesso do cliente" },
            { value: "100%", label: "White-label" },
            { value: "0 ligue", label: "Autoatendimento" },
        ],
    },
    {
        id: "tools",
        tabLabel: "Tools",
        title: "App para equipes em campo — vistorias e operações",
        description:
            "O Tools mantém sua equipe de campo sincronizada com a gestão. Tudo registrado digitalmente, sem papel e sem retrabalho.",
        points: [
            "Vistorias com fotos",
            "Checklists operacionais",
            "Assinatura eletrônica",
        ],
        cta: "Explorar Tools",
        href: "/aplicativos-para-imobiliarias",
        metrics: [
            { value: "100%", label: "Sincronizado" },
            { value: "0 papel", label: "Vistorias digitais" },
            { value: "D+0", label: "Atualização" },
        ],
    },
];


export default function Features() {
    const [activeTabId, setActiveTabId] = useState(featureTabs[0]?.id ?? "");
    const activeTab = featureTabs.find((item) => item.id === activeTabId) ?? featureTabs[0]!;

    return (
        <section className={styles.features}>
            <div className={styles.container}>
                <div className={styles.tabs} aria-label="Áreas da plataforma">
                    {featureTabs.map((item) => (
                        <button
                            key={item.id}
                            className={`${styles.tab} ${activeTabId === item.id ? styles.activeTab : ""}`}
                            onClick={() => setActiveTabId(item.id)}
                            type="button"
                        >
                            {item.tabLabel}
                        </button>
                    ))}
                </div>

                <div className={styles.content}>
                    <div className={styles.textColumn}>
                        <h3 className={styles.title}>{activeTab.title}</h3>
                        <p className={styles.description}>{activeTab.description}</p>

                        <ul className={styles.points}>
                            {activeTab.points.map((point) => (
                                <li key={point}>{point}</li>
                            ))}
                        </ul>

                        <a className={styles.cta} href={activeTab.href}>
                            {activeTab.cta} <span aria-hidden="true">→</span>
                        </a>
                    </div>

                    <div className={styles.visualCard}>
                        <Image
                            src={`/images/features/${activeTab.id}.png`}
                            alt={activeTab.title}
                            className={styles.featureImage}
                            width={1200}
                            height={800}
                            style={{ width: "100%", height: "auto" }}
                        />
                    </div>
                </div>
            </div>
        </section>
    );
}
